import { useMemo, useState } from "react";
import { Card, Button, Spinner, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faChevronDown, faChevronUp, faLock, faPlay, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { baseURL, CHAPTERID } from "../../API/API";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";

function normalizeLesson(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name ?? raw.lesson_name ?? "درس",
    description: raw.description ?? raw.lesson_description ?? "",
    chapter_id: raw.chapter_id,
    academic_year_id: raw.academic_year_id,
    sort_order: raw.sort_order ?? raw.order ?? raw.id,
  };
}

export default function BundleContentAccordion({
  chapters = [],
  hasAccess = false,
  title = "محتوى الباقة",
  returnTo,
  returnLabel = "العودة للباقة",
}) {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const userKey = auth?.user?.email || "guest";
  const [expandedId, setExpandedId] = useState(null);
  const [lessonsByChapter, setLessonsByChapter] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  const isLessonDone = useMemo(
    () => (lessonId) => localStorage.getItem(`completed-lesson-${userKey}-${lessonId}`) === "true",
    [userKey]
  );

  const getChapterProgress = (chapterId) => {
    const lessons = lessonsByChapter[chapterId] || [];
    if (!lessons.length) return { completed: 0, total: 0, percent: 0 };
    const completed = lessons.filter((lesson) => isLessonDone(lesson.id)).length;
    return { completed, total: lessons.length, percent: Math.round((completed / lessons.length) * 100) };
  };

  const bundleProgress = useMemo(() => {
    const allLessons = Object.values(lessonsByChapter).flat();
    if (!allLessons.length) return { completed: 0, total: 0, percent: 0 };
    const completed = allLessons.filter((lesson) => isLessonDone(lesson.id)).length;
    return { completed, total: allLessons.length, percent: Math.round((completed / allLessons.length) * 100) };
  }, [lessonsByChapter, isLessonDone]);

  const loadChapterLessons = async (chapter) => {
    if (lessonsByChapter[chapter.id]) return lessonsByChapter[chapter.id];
    setLoadingId(chapter.id);
    try {
      const res = await axiosPrivate.get(`${baseURL}/${CHAPTERID}/${chapter.id}`);
      const lessons = (res.data?.data || []).map(normalizeLesson).filter(Boolean);
      setLessonsByChapter((prev) => ({ ...prev, [chapter.id]: lessons }));
      return lessons;
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل تحميل دروس الشهر");
      setLessonsByChapter((prev) => ({ ...prev, [chapter.id]: [] }));
      return [];
    } finally {
      setLoadingId(null);
    }
  };

  const toggleChapter = async (chapter) => {
    if (!hasAccess) return;
    if (expandedId === chapter.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(chapter.id);
    await loadChapterLessons(chapter);
  };

  return (
    <Card className="border shadow-sm rounded-4 p-4 mb-4 text-end">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-4">
        {hasAccess && bundleProgress.total > 0 && (
          <div className="text-start" style={{ minWidth: 210 }}>
            <div className="fw-bold small mb-2">إكمال الباقة {bundleProgress.percent}%</div>
            <ProgressBar now={bundleProgress.percent} style={{ height: 10, borderRadius: 999 }} />
            <div className="text-muted small mt-1">شاهدت {bundleProgress.completed} من أصل {bundleProgress.total} درس</div>
          </div>
        )}
        <h3 className="fw-bold text-dark m-0 d-flex align-items-center gap-2 justify-content-end">
          <span>{title}</span>
          <FontAwesomeIcon icon={hasAccess ? faUnlock : faLock} className={hasAccess ? "text-success" : "text-muted"} />
        </h3>
      </div>

      <div className="d-flex flex-column gap-3">
        {chapters.map((chapter, index) => {
          const lessons = lessonsByChapter[chapter.id] || [];
          const isExpanded = expandedId === chapter.id;
          const chapterProgress = getChapterProgress(chapter.id);
          return (
            <div key={chapter.id} className="border bg-white shadow-sm overflow-hidden" style={{ borderRadius: 8 }}>
              <button
                type="button"
                onClick={() => toggleChapter(chapter)}
                className="w-100 border-0 bg-white p-3 text-end d-flex justify-content-between align-items-center gap-3"
                style={{ cursor: hasAccess ? "pointer" : "default" }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-circle fw-bold d-flex align-items-center justify-content-center" style={{ width: 42, height: 42, backgroundColor: hasAccess ? "#dcfce7" : "#f1f5f9", color: hasAccess ? "#15803d" : "#64748b" }}>
                    {index + 1}
                  </div>
                  <div>
                    <h5 className="fw-bold m-0 text-dark">{chapter.name}</h5>
                    <span className="text-muted small">{chapter.teacher_name || "الأدمن"}{chapter.academic_year_name ? ` - ${chapter.academic_year_name}` : ""}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {hasAccess && chapterProgress.total > 0 && <span className="badge bg-success-subtle text-success border">{chapterProgress.percent}%</span>}
                  <span className="fw-bold text-secondary">{Number(chapter.price || 0).toFixed(0)} ج.م</span>
                  {hasAccess && <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="text-muted" />}
                </div>
              </button>

              {hasAccess && isExpanded && (
                <div className="border-top p-3" style={{ backgroundColor: "#f8fafc" }}>
                  {chapterProgress.total > 0 && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between small fw-bold mb-2">
                        <span>{chapterProgress.percent}% مكتمل</span>
                        <span>شاهدت {chapterProgress.completed} / {chapterProgress.total}</span>
                      </div>
                      <ProgressBar now={chapterProgress.percent} style={{ height: 9, borderRadius: 999 }} />
                    </div>
                  )}

                  {loadingId === chapter.id ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /> <span className="text-muted me-2">جاري تحميل الدروس...</span></div>
                  ) : lessons.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {lessons.map((lesson, lessonIndex) => {
                        const done = isLessonDone(lesson.id);
                        return (
                          <div key={lesson.id} className="bg-white border d-flex justify-content-between align-items-center gap-3 p-3" style={{ borderRadius: 8 }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="rounded-circle fw-bold d-flex align-items-center justify-content-center" style={{ width: 34, height: 34, backgroundColor: done ? "#dcfce7" : "#f8fafc", color: done ? "#15803d" : "#0f172a" }}>
                                {done ? <FontAwesomeIcon icon={faCheckCircle} /> : lessonIndex + 1}
                              </div>
                              <div>
                                <div className="fw-bold text-dark">{lesson.name}</div>
                                <div className="text-muted small">{lesson.description || "شرح الدرس"}</div>
                              </div>
                            </div>
                            <Link
                              to={`/lesson/${lesson.id}`}
                              state={{ academicYearId: chapter.academic_year_id, chapterId: chapter.id, returnTo, returnLabel }}
                              className="text-decoration-none"
                            >
                              <Button className="rounded-pill px-3 fw-bold" style={{ backgroundColor: "var(--brand-300)", borderColor: "var(--brand-300)" }}>
                                <FontAwesomeIcon icon={faPlay} /> ابدأ الدرس
                              </Button>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-muted text-center py-3">لا توجد دروس منشورة لهذا الشهر حاليا.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!hasAccess && <div className="alert alert-warning mt-4 mb-0">بعد قبول التحويل هتظهر الشهور والدروس هنا، والدرس نفسه يفتح في صفحة الدرس.</div>}
    </Card>
  );
}